(() => {
  'use strict';

  const STORAGE_KEY = 'perfumessoRecentlyViewedV2';
  const LEGACY_STORAGE_KEY = 'recentlyViewed';
  const MAX_STORED_PRODUCTS = 8;
  const MAX_DISPLAYED_PRODUCTS = 4;

  function getHandleFromUrl(url) {
    try {
      const pathname = new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
      const match = pathname.match(/\/products\/([^/]+)$/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  }

  function normalizeItem(item, rootUrl) {
    if (!item || typeof item !== 'object') return null;

    const handle = String(
      item.handle || getHandleFromUrl(item.url || '')
    ).trim();

    if (!handle) return null;

    const vendor = typeof item.vendor === 'string'
      ? item.vendor
      : '';

    const rawTitle = typeof item.title === 'string'
      ? item.title
      : '';

    return {
      id: item.id ? String(item.id) : '',
      handle,
      url: item.url || `${rootUrl}products/${handle}`,
      title: cleanDisplayTitle(rawTitle, vendor),
      vendor,
      image: item.image || item.img || '',
      legacyPrice: typeof item.price === 'string'
        ? item.price
        : ''
    };
  }

  function readHistory(rootUrl) {
    try {
      const raw =
        localStorage.getItem(STORAGE_KEY) ||
        localStorage.getItem(LEGACY_STORAGE_KEY);

      if (!raw) return [];

      const parsed = JSON.parse(raw);

      if (!Array.isArray(parsed)) return [];

      return parsed
        .map((item) => normalizeItem(item, rootUrl))
        .filter(Boolean);

    } catch (error) {
      console.warn(
        'Nie udało się odczytać ostatnio oglądanych produktów.',
        error
      );

      return [];
    }
  }

  function saveHistory(items) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(items.slice(0, MAX_STORED_PRODUCTS))
      );

      localStorage.removeItem(LEGACY_STORAGE_KEY);

    } catch (error) {
      console.warn(
        'Nie udało się zapisać ostatnio oglądanych produktów.',
        error
      );
    }
  }

  function getProductJsonUrl(handle, rootUrl) {
    const normalizedRoot = rootUrl.endsWith('/')
      ? rootUrl
      : `${rootUrl}/`;

    return `${normalizedRoot}products/${encodeURIComponent(handle)}.js`;
  }

  function formatMoney(cents) {
    const locale =
      document.documentElement.lang || 'pl-PL';

    const currency =
      window.Shopify?.currency?.active || 'PLN';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(cents / 100);

    } catch (_) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) {
      return tags.map(String);
    }

    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }

    return [];
  }

  function hasSaleVariant(variants) {
    return variants.some((variant) => {
      const price = Number(variant.price);
      const compareAt = Number(variant.compare_at_price);

      return (
        Number.isFinite(compareAt) &&
        compareAt > price
      );
    });
  }

  function getBadge(data) {
    const tags = normalizeTags(data.tags);

    if (tags.includes('Wkrótce dostępne')) {
      return {
        text: 'Zapowiedź',
        cls: 'badge--soon',
        rgb: '30, 58, 138',
        color: '#1E3A8A'
      };
    }

    if (data.available === false) {
      return {
        text: 'Wyprzedane',
        cls: 'badge--soldout',
        rgb: '39, 43, 48',
        color: '#272B30'
      };
    }

    if (tags.includes('3+1')) {
      return {
        text: '3+1',
        cls: 'badge--3plus1',
        rgb: '152, 27, 53',
        color: '#981B35'
      };
    }

    if (data.isOnSale && data.available !== false) {
      return {
        text: 'W promocji',
        cls: 'badge--promo',
        rgb: '152, 27, 53',
        color: '#981B35'
      };
    }

    if (tags.includes('Zestawy odlewek')) {
      return {
        text: 'Zestaw taniej',
        cls: 'badge--zestaw',
        rgb: '89, 33, 63',
        color: '#59213F'
      };
    }

    if (tags.includes('Premiera 2026')) {
      return {
        text: "Premiera '26",
        cls: 'badge--premiere',
        rgb: '37, 42, 92',
        color: '#252A5C'
      };
    }

    if (tags.includes('Bestseller')) {
      return {
        text: 'Bestseller',
        cls: 'badge--bestseller',
        rgb: '167, 68, 0',
        color: '#A74400'
      };
    }

    if (tags.includes('Nowość')) {
      return {
        text: 'Nowość',
        cls: 'badge--new',
        rgb: '7, 87, 68',
        color: '#075744'
      };
    }

    return null;
  }

  function cleanDisplayTitle(title, vendor) {
    let output = String(title || '')
      .replace(
        /\s[-–—]\s(?:Odlewka perfum|Odlewka mgiełki(?: zapachowej)?|Zestaw odlewek)\s*$/i,
        ''
      )
      .trim();

    const normalizedVendor =
      String(vendor || '').trim();

    if (
      normalizedVendor &&
      output
        .toLowerCase()
        .startsWith(`${normalizedVendor.toLowerCase()} `)
    ) {
      output = output
        .slice(normalizedVendor.length)
        .trim();
    }

    return output;
  }

  async function loadCurrentProductData(item, rootUrl) {
    try {
      const response = await fetch(
        getProductJsonUrl(item.handle, rootUrl),
        {
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      const variants =
        Array.isArray(data.variants)
          ? data.variants
          : [];

      const prices = variants
        .map((variant) => Number(variant.price))
        .filter(Number.isFinite);

      const minimumPrice =
        prices.length
          ? Math.min(...prices)
          : null;

      const maximumPrice =
        prices.length
          ? Math.max(...prices)
          : null;

      const handle =
        data.handle || item.handle;

      const vendor =
        data.vendor ||
        item.vendor ||
        '';

      const title =
        cleanDisplayTitle(
          data.title || item.title,
          vendor
        );

      return {
        id: data.id
          ? String(data.id)
          : item.id,

        handle,

        url: `${rootUrl}products/${handle}`,

        title,

        vendor,

        image:
          data.featured_image ||
          data.images?.[0] ||
          item.image,

        tags: data.tags,

        available:
          typeof data.available === 'boolean'
            ? data.available
            : variants.some((variant) => variant.available),

        isOnSale:
          hasSaleVariant(variants),

        priceText:
          minimumPrice === null
            ? item.legacyPrice
              ? `Od ${item.legacyPrice}`
              : ''
            : minimumPrice !== maximumPrice
              ? `Od ${formatMoney(minimumPrice)}`
              : `Od ${formatMoney(minimumPrice)}`
      };

    } catch (_) {
      const vendor =
        item.vendor || '';

      const title =
        cleanDisplayTitle(
          item.title,
          vendor
        );

      return {
        id: item.id,
        handle: item.handle,
        url: item.url,
        title,
        vendor,
        image: item.image,
        tags: [],
        available: true,
        isOnSale: false,
        priceText:
          item.legacyPrice
            ? `Od ${item.legacyPrice}`
            : ''
      };
    }
  }

  function el(tagName, className) {
    const node =
      document.createElement(tagName);

    if (className) {
      node.className = className;
    }

    return node;
  }

  function createFavoriteButton(productData) {
    const button =
      el('button', 'fav-btn');

    button.type = 'button';

    button.dataset.productHandle =
      productData.handle;

    button.dataset.productId =
      productData.id || '';

    button.setAttribute(
      'aria-label',
      'Dodaj do ulubionych'
    );

    button.setAttribute(
      'aria-pressed',
      'false'
    );

    button.innerHTML = `
      <svg
        class="fav-heart"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z"></path>
      </svg>
    `;

    return button;
  }

  function createBadge(productData, badgePosition) {
    const badgeData =
      getBadge(productData);

    if (!badgeData) return null;

    const position =
      String(badgePosition || '').trim();

    const wrapper =
      el(
        'div',
        `pdp-v5-card__badge card__badge ${position}`.trim()
      );

    const badge =
      el(
        'span',
        `badge badge--bottom-left ${badgeData.cls} badge--modern-glass`
      );

    badge.style.setProperty(
      '--badge-rgb',
      badgeData.rgb
    );

    badge.style.setProperty(
      '--badge-color',
      badgeData.color
    );

    badge.textContent =
      badgeData.text;

    wrapper.appendChild(badge);

    return wrapper;
  }

  function createProductCard(productData, badgePosition) {
    if (
      !productData.title ||
      !productData.url
    ) {
      return null;
    }

    const li =
      el('li');

    const article =
      el(
        'article',
        'pdp-v5-card pdp-v5-card--editorial'
      );

    const media =
      el(
        'div',
        'pdp-v5-card__media'
      );

    const imageStage =
      el(
        'div',
        'pdp-v5-card__image-stage'
      );

    if (productData.image) {
      const image =
        document.createElement('img');

      image.src =
        productData.image;

      image.alt =
        productData.title || '';

      image.className =
        'pdp-v5-card__image pdp-v5-card__image--primary motion-reduce';

      image.loading =
        'lazy';

      image.width =
        640;

      image.height =
        640;

      imageStage.appendChild(image);

    } else {
      const placeholder =
        el(
          'div',
          'pdp-v5-card__image-placeholder'
        );

      placeholder.textContent =
        'Brak zdjęcia';

      imageStage.appendChild(placeholder);
    }

    media.appendChild(imageStage);

    const wishlist =
      el(
        'div',
        'pdp-v5-card__wishlist'
      );

    wishlist.removeAttribute(
      'aria-hidden'
    );

    wishlist.appendChild(
      createFavoriteButton(productData)
    );

    media.appendChild(wishlist);

    const badge =
      createBadge(
        productData,
        badgePosition
      );

    if (badge) {
      media.appendChild(badge);
    }

    article.appendChild(media);

    const body =
      el(
        'div',
        'pdp-v5-card__body'
      );

    if (productData.vendor) {
      const vendor =
        el(
          'div',
          'pdp-v5-card__vendor'
        );

      vendor.textContent =
  String(productData.vendor).trim().toLowerCase() === 'multibrand'
    ? 'Różne marki'
    : productData.vendor;

      body.appendChild(vendor);
    }

    const cleanTitle =
      cleanDisplayTitle(
        productData.title,
        productData.vendor
      );

    const title =
      el(
        'h3',
        'pdp-v5-card__title'
      );

    title.textContent =
      cleanTitle;

    body.appendChild(title);

    const priceZone =
      el(
        'div',
        'pdp-v5-card__price-zone'
      );

    if (productData.priceText) {
      const price =
        el(
          'div',
          'price pdp-v5-card__price'
        );

      const regular =
        el(
          'div',
          'price__regular'
        );

      const item =
        el(
          'span',
          'price-item price-item--regular'
        );

      item.textContent =
        productData.priceText;

      regular.appendChild(item);
      price.appendChild(regular);
      priceZone.appendChild(price);
    }

    body.appendChild(priceZone);
    article.appendChild(body);

    const stretchedLink =
      el(
        'a',
        'pdp-v5-card__stretched-link'
      );

    stretchedLink.href =
      productData.url;

    const accessibleTitle =
      cleanDisplayTitle(
        productData.title,
        productData.vendor
      );

    stretchedLink.setAttribute(
      'aria-label',
      `Zobacz produkt: ${accessibleTitle}`
    );

    const linkLabel =
      el(
        'span',
        'visually-hidden'
      );

    linkLabel.textContent =
      `Zobacz produkt: ${accessibleTitle}`;

    stretchedLink.appendChild(
      linkLabel
    );

    article.appendChild(
      stretchedLink
    );

    li.appendChild(article);

    return li;
  }

  function readConfig(section) {
    const configElement =
      section.querySelector(
        '[data-recently-viewed-config]'
      );

    if (!configElement) {
      return null;
    }

    try {
      return JSON.parse(
        configElement.textContent
      );

    } catch (error) {
      console.warn(
        'Nie udało się odczytać konfiguracji ostatnio oglądanych.',
        error
      );

      return null;
    }
  }

  async function renderSection(
    section,
    list,
    config,
    productsToDisplay,
    rootUrl
  ) {
    if (
      section.dataset.recentlyViewedInitialized === 'true'
    ) {
      return;
    }

    section.dataset.recentlyViewedInitialized =
      'true';

    const loadedProducts =
      await Promise.all(
        productsToDisplay.map((item) =>
          loadCurrentProductData(
            item,
            rootUrl
          )
        )
      );

    const fragment =
      document.createDocumentFragment();

    loadedProducts.forEach(
      (productData) => {
        const card =
          createProductCard(
            productData,
            config.badgePosition
          );

        if (card) {
          fragment.appendChild(card);
        }
      }
    );

    if (fragment.childNodes.length) {
      list.replaceChildren(fragment);

      section.hidden =
        false;

      document.dispatchEvent(
        new CustomEvent(
          'product-grid:updated'
        )
      );
    }
  }

  function initializeSection(section) {
    if (
      !section ||
      section.dataset.recentlyViewedPrepared === 'true'
    ) {
      return;
    }

    const list =
      section.querySelector(
        '[data-recently-viewed-list]'
      );

    const config =
      readConfig(section);

    if (
      !list ||
      !config?.product?.handle
    ) {
      return;
    }

    section.dataset.recentlyViewedPrepared =
      'true';

    const rootUrl =
      config.rootUrl || '/';

    const currentProduct =
      normalizeItem(
        config.product,
        rootUrl
      );

    if (!currentProduct) {
      return;
    }

    const history =
      readHistory(rootUrl);

    const productsToDisplay =
      history
        .filter(
          (item) =>
            item.handle !==
            currentProduct.handle
        )
        .slice(
          0,
          MAX_DISPLAYED_PRODUCTS
        );

    const updatedHistory =
      history.filter(
        (item) =>
          item.handle !==
          currentProduct.handle
      );

    updatedHistory.unshift(
      currentProduct
    );

    saveHistory(
      updatedHistory
    );

    if (!productsToDisplay.length) {
      return;
    }

    const loadProducts = () =>
      renderSection(
        section,
        list,
        config,
        productsToDisplay,
        rootUrl
      );

    const sentinel =
      section.previousElementSibling?.matches(
        '[data-recently-viewed-sentinel]'
      )
        ? section.previousElementSibling
        : null;

    if (
      !sentinel ||
      !('IntersectionObserver' in window)
    ) {
      loadProducts();
      return;
    }

    const observer =
      new IntersectionObserver(
        (entries) => {
          if (
            !entries.some(
              (entry) =>
                entry.isIntersecting
            )
          ) {
            return;
          }

          observer.disconnect();
          loadProducts();
        },
        {
          rootMargin: '800px 0px',
          threshold: 0
        }
      );

    observer.observe(
      sentinel
    );
  }

  function initializeAll(root = document) {
    if (
      root.matches?.(
        '[data-recently-viewed]'
      )
    ) {
      initializeSection(root);
    }

    root
      .querySelectorAll?.(
        '[data-recently-viewed]'
      )
      .forEach(
        initializeSection
      );
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll(),
      { once: true }
    );

  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) =>
      initializeAll(event.target)
  );
})();